import React, { useState } from 'react';
import api from '../../api/client';
import endpoints from '../../api/endpoints';
import toast from 'react-hot-toast';
import { Save, Trash2, AlertTriangle } from 'lucide-react';

const SettingsTab = () => {
  const [retentionDays, setRetentionDays] = useState(90);
  const [cleaning, setCleaning] = useState(false);

  const handleCleanup = async () => {
    if (!window.confirm(`Are you sure you want to delete all conversations older than ${retentionDays} days? This cannot be undone.`)) {
      return;
    }

    setCleaning(true);
    try {
      const response = await api.post(endpoints.conversations.cleanup, { retentionDays });
      toast.success(`Cleanup completed. ${response.data.deletedThreads} conversations deleted.`);
    } catch (error) {
      console.error('Error during cleanup:', error);
      toast.error('Cleanup failed');
    } finally {
      setCleaning(false);
    }
  };

  return (
    <div className="settings-tab">
      <h2 className="tab-section-title">System Settings</h2>

      <div className="tab-section">
        <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: 'var(--spacing-md)' }}>
          Data Retention Policy
        </h3>
        <p style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-lg)' }}>
          Configure how long conversation history is retained in the database. Older conversations will be automatically cleaned up.
        </p>

        <div style={{ maxWidth: '500px' }}>
          <label style={{ display: 'block', marginBottom: 'var(--spacing-sm)', fontWeight: 500 }}>
            Retention Period (days)
          </label>
          <div style={{ display: 'flex', gap: 'var(--spacing-md)', alignItems: 'center' }}>
            <input
              type="number"
              className="filter-input"
              value={retentionDays}
              onChange={(e) => setRetentionDays(parseInt(e.target.value) || 0)}
              min="1"
              max="3650"
              style={{ flex: 1 }}
            />
            <button
              className="action-button primary"
              onClick={() => {
                localStorage.setItem('conversationRetentionDays', retentionDays);
                toast.success('Retention policy updated');
              }}
            >
              <Save size={16} /> Save Policy
            </button>
          </div>
          <small style={{ display: 'block', marginTop: 'var(--spacing-sm)', color: 'var(--color-text-secondary)' }}>
            Conversations older than {retentionDays} days will be eligible for cleanup
          </small>
        </div>
      </div>

      <div className="tab-section" style={{ borderTop: '1px solid var(--color-border)', paddingTop: 'var(--spacing-xl)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--spacing-md)', padding: 'var(--spacing-lg)', background: '#fff3cd', borderRadius: 'var(--radius-md)', border: '1px solid #ffc107', marginBottom: 'var(--spacing-lg)' }}>
          <AlertTriangle size={24} style={{ color: '#856404', flexShrink: 0 }} />
          <div>
            <strong style={{ display: 'block', marginBottom: '4px', color: '#856404' }}>
              Danger Zone
            </strong>
            <p style={{ margin: 0, fontSize: '0.875rem', color: '#856404' }}>
              These actions are irreversible. Make sure you understand the impact before proceeding.
            </p>
          </div>
        </div>

        <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: 'var(--spacing-md)' }}>
          Manual Cleanup
        </h3>
        <p style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-lg)' }}>
          Manually trigger a cleanup of old conversations based on the retention policy above.
        </p>

        <button
          className="action-button danger"
          onClick={handleCleanup}
          disabled={cleaning || retentionDays < 1}
          style={{ padding: '12px 24px' }}
        >
          <Trash2 size={16} />
          {cleaning ? 'Cleaning up...' : `Delete conversations older than ${retentionDays} days`}
        </button>
      </div>

      <div className="tab-section" style={{ borderTop: '1px solid var(--color-border)', paddingTop: 'var(--spacing-xl)' }}>
        <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: 'var(--spacing-md)' }}>
          System Information
        </h3>
        <table className="data-table" style={{ maxWidth: '600px' }}>
          <tbody>
            <tr>
              <td><strong>Database Type</strong></td>
              <td>Azure Cosmos DB (MongoDB API)</td>
            </tr>
            <tr>
              <td><strong>Message Format</strong></td>
              <td>Markdown</td>
            </tr>
            <tr>
              <td><strong>Storage Mode</strong></td>
              <td>Append-only text field</td>
            </tr>
            <tr>
              <td><strong>Conversation Logging</strong></td>
              <td><span className="badge success">Active</span></td>
            </tr>
            <tr>
              <td><strong>Few-Shot Learning</strong></td>
              <td><span className="badge success">Enabled</span></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="tab-section" style={{ borderTop: '1px solid var(--color-border)', paddingTop: 'var(--spacing-xl)' }}>
        <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: 'var(--spacing-md)' }}>
          Quick Actions
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 'var(--spacing-md)' }}>
          <div style={{ padding: 'var(--spacing-lg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
            <h4 style={{ margin: '0 0 8px', fontSize: '1rem' }}>Export All Data</h4>
            <p style={{ margin: '0 0 12px', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
              Download all conversations and feedback as JSON
            </p>
            <button className="action-button" disabled>
              Coming Soon
            </button>
          </div>

          <div style={{ padding: 'var(--spacing-lg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
            <h4 style={{ margin: '0 0 8px', fontSize: '1rem' }}>Generate Reports</h4>
            <p style={{ margin: '0 0 12px', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
              Create usage and performance reports
            </p>
            <button className="action-button" disabled>
              Coming Soon
            </button>
          </div>

          <div style={{ padding: 'var(--spacing-lg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
            <h4 style={{ margin: '0 0 8px', fontSize: '1rem' }}>System Health</h4>
            <p style={{ margin: '0 0 12px', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
              Check database and API connection status
            </p>
            <button className="action-button" disabled>
              Coming Soon
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsTab;
